// Studio team Inc 3 — cohost (team) backchannel over the LiveKit data channel.
//
// Pure wire-format contract for the off-air producer messaging: a silent,
// ephemeral channel between the people on air (host + cohosts), addressed to
// publisher identities at the SFU so viewers never receive the bytes. Mirrors
// social.ts: the seam is the single source of truth for the shape, and decode
// returns null on anything malformed so callers never re-validate in parallel.

/** Data-channel topic every cohost-chat packet is published and filtered under. */
export const COHOST_CHAT_TOPIC = 'cohost';

/** One off-air team message. Ephemeral — never persisted. */
export interface CohostChatMessage {
  /** uuid — dedupe key + React key. */
  id: string;
  /** Sender's participant identity (makeIdentity-derived); display/debug only. */
  senderId: string;
  /** Sender's on-stream/display name, for the panel. */
  senderName: string;
  /** Trimmed, length-capped body. */
  body: string;
  /** Date.now() at send, for ordering. */
  sentAt: number;
}

const MAX_BODY = 500;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

/** Serialise a message to a UTF-8 JSON byte payload for `publishData`. */
export function encodeCohostChat(msg: CohostChatMessage): Uint8Array {
  return encoder.encode(JSON.stringify(msg));
}

/**
 * Parse + validate a received payload. Returns the message, or `null` for
 * anything malformed (wrong topic decoded here would already be filtered by the
 * caller; this guards bad JSON / shape). Single source of truth for the wire
 * shape — callers render only non-null results and do NOT re-validate.
 */
export function decodeCohostChat(bytes: Uint8Array): CohostChatMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(decoder.decode(bytes));
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const o = parsed as Record<string, unknown>;
  if (typeof o.id !== 'string' || o.id === '') return null;
  if (typeof o.senderId !== 'string') return null;
  if (typeof o.senderName !== 'string') return null;
  if (typeof o.body !== 'string') return null;
  if (typeof o.sentAt !== 'number' || !Number.isFinite(o.sentAt)) return null;
  const body = o.body.trim();
  if (!body) return null;
  return {
    id: o.id,
    senderId: o.senderId,
    senderName: o.senderName,
    body: body.slice(0, MAX_BODY),
    sentAt: o.sentAt,
  };
}
